import { useEffect, useRef, useState } from "react";
import { useAnnouncementMediaUrl } from "@/components/AnnouncementMedia";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
    SpeakerHigh, SpeakerSlash, ArrowClockwise, Play, X, Warning,
} from "@phosphor-icons/react";

const PRIORITY = {
    urgent: { label: "Urgent", cls: "bg-[#E6002C] text-white border-0" },
    important: { label: "Important", cls: "bg-amber-400 text-[#1F2A44] border-0" },
    normal: { label: "Notice", cls: "bg-white/15 text-white border border-white/20" },
};

/**
 * Immersive full-screen announcement for image/video notices. The media fills
 * the viewport with `object-contain` (never stretched or cropped) and the
 * required controls stay pinned and reachable on phones.
 */
export const AnnouncementFullscreen = ({
    announcement, mustAcknowledge, confirmed, onConfirm, onAcknowledge, busy,
    onClose, counterText,
}) => {
    const media = announcement.media;
    const { url, failed } = useAnnouncementMediaUrl(announcement.id, media);
    const isVideo = media?.type === "video";
    const videoRef = useRef(null);
    const [muted, setMuted] = useState(true);
    const [played, setPlayed] = useState(false);
    const [blocked, setBlocked] = useState(false);
    const [unplayable, setUnplayable] = useState(false);

    // Autoplay muted (mobile policy); if the browser still refuses, offer Play.
    useEffect(() => {
        if (!isVideo || !url) return;
        const el = videoRef.current;
        if (!el) return;
        el.muted = true;
        const attempt = el.play();
        if (attempt?.catch) attempt.catch(() => setBlocked(true));
    }, [isVideo, url]);

    const start = () => {
        const el = videoRef.current;
        if (!el) return;
        setBlocked(false);
        el.currentTime = 0;
        el.play().catch(() => setBlocked(true));
    };

    const toggleSound = () => {
        const el = videoRef.current;
        if (!el) return;
        const next = !el.muted;
        el.muted = next;
        setMuted(next);
        if (!next && el.paused) el.play().catch(() => {});
    };

    // Acknowledgment for a video is only offered after one full playback —
    // unless this device simply cannot play the file, in which case blocking
    // acknowledgment forever would lock the employee out of the portal.
    const ackBlockedByVideo = mustAcknowledge && isVideo && !played && !unplayable && !failed;

    return (
        <div className="fixed inset-0 z-[110] bg-black text-white flex flex-col"
            style={{ height: "100dvh" }} data-testid="announcement-fullscreen">
            <div className="flex items-start gap-3 px-4 sm:px-6 pt-4 pb-3"
                style={{ paddingTop: "calc(1rem + env(safe-area-inset-top))" }}>
                <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2">
                        <Badge className={PRIORITY[announcement.priority]?.cls || PRIORITY.normal.cls}
                            data-testid="fullscreen-priority-badge">
                            {(PRIORITY[announcement.priority] || PRIORITY.normal).label}
                        </Badge>
                        {counterText && (
                            <span className="text-[11px] text-white/60" data-testid="fullscreen-counter">
                                {counterText}
                            </span>
                        )}
                    </div>
                    <h2 className="mt-1.5 text-base md:text-lg font-semibold leading-snug break-words"
                        style={{ fontFamily: "Outfit" }} data-testid="fullscreen-title">
                        {announcement.title}
                    </h2>
                </div>
                {onClose && (
                    <button onClick={onClose} className="text-white/70 hover:text-white p-1"
                        data-testid="fullscreen-close-button" aria-label="Close">
                        <X size={22} />
                    </button>
                )}
            </div>

            <div className="relative flex-1 min-h-0 flex items-center justify-center px-2 sm:px-6">
                {failed ? (
                    <div className="flex items-center gap-2 text-sm text-amber-300" data-testid="fullscreen-media-error">
                        <Warning size={18} /> The attachment could not be loaded right now.
                    </div>
                ) : !url ? (
                    <div className="h-10 w-10 rounded-full border-2 border-white/20 border-t-white animate-spin"
                        data-testid="fullscreen-media-loading" />
                ) : isVideo ? (
                    <>
                        <video ref={videoRef} src={url} playsInline muted={muted} autoPlay
                            onEnded={() => setPlayed(true)} onPlay={() => setBlocked(false)}
                            onError={() => setUnplayable(true)}
                            className="h-full w-full object-contain"
                            data-testid="fullscreen-video" />
                        {unplayable && (
                            <div className="absolute inset-x-4 top-1/2 -translate-y-1/2 text-center text-sm text-amber-300"
                                data-testid="fullscreen-video-unplayable">
                                <Warning size={18} className="inline mr-1.5 -mt-0.5" />
                                This video can't be played on this device.
                            </div>
                        )}
                        {blocked && !unplayable && (
                            <button onClick={start}
                                className="absolute inset-0 m-auto h-16 w-16 rounded-full bg-white/15 backdrop-blur flex items-center justify-center"
                                data-testid="fullscreen-play-button" aria-label="Play video">
                                <Play size={28} weight="fill" />
                            </button>
                        )}
                        <div className="absolute bottom-3 right-3 sm:right-8 flex gap-2">
                            <button onClick={toggleSound}
                                className="h-11 w-11 rounded-full bg-white/15 hover:bg-white/25 backdrop-blur flex items-center justify-center transition-colors"
                                data-testid="fullscreen-sound-toggle"
                                aria-label={muted ? "Turn sound on" : "Turn sound off"}>
                                {muted ? <SpeakerSlash size={20} /> : <SpeakerHigh size={20} />}
                            </button>
                            <button onClick={start}
                                className="h-11 w-11 rounded-full bg-white/15 hover:bg-white/25 backdrop-blur flex items-center justify-center transition-colors"
                                data-testid="fullscreen-replay-button" aria-label="Replay video">
                                <ArrowClockwise size={20} />
                            </button>
                        </div>
                    </>
                ) : (
                    <img src={url} alt={announcement.title}
                        className="h-full w-full object-contain"
                        data-testid="fullscreen-image" />
                )}
            </div>

            <div className="px-4 sm:px-6 pt-3 pb-4 bg-black/60 backdrop-blur border-t border-white/10"
                style={{ paddingBottom: "calc(1rem + env(safe-area-inset-bottom))" }}>
                {announcement.message ? (
                    <p className="text-sm text-white/85 leading-relaxed whitespace-pre-wrap break-words max-h-24 overflow-y-auto"
                        data-testid="fullscreen-message">
                        {announcement.message}
                    </p>
                ) : null}

                {mustAcknowledge ? (
                    <div className={announcement.message ? "mt-3" : ""}>
                        <label className="flex items-start gap-2.5 cursor-pointer select-none">
                            <input type="checkbox" checked={confirmed} disabled={ackBlockedByVideo}
                                onChange={(e) => onConfirm(e.target.checked)}
                                className="mt-0.5 h-4 w-4 accent-[#0066D9]"
                                data-testid="fullscreen-confirm-checkbox" />
                            <span className="text-sm">I have read and understood</span>
                        </label>
                        <div className="mt-3 flex flex-col sm:flex-row sm:items-center gap-2.5">
                            <Button onClick={onAcknowledge} disabled={busy || !confirmed || ackBlockedByVideo}
                                className="bg-[#0066D9] hover:bg-[#0052ad] text-white h-12 w-full sm:w-auto"
                                data-testid="fullscreen-acknowledge-button">
                                {busy ? "Sending…" : "Acknowledge & Continue"}
                            </Button>
                            <span className="text-xs text-white/60" data-testid="fullscreen-ack-note">
                                {ackBlockedByVideo
                                    ? "Watch the video all the way through to acknowledge it."
                                    : unplayable || failed
                                        ? "The video could not be played here — you can still acknowledge this notice."
                                        : "You must acknowledge this notice to continue using the portal."}
                            </span>
                        </div>
                    </div>
                ) : (
                    <div className={`flex flex-col sm:flex-row sm:items-center gap-2.5 ${announcement.message ? "mt-3" : ""}`}>
                        <Button onClick={onClose}
                            className="bg-white text-[#003366] hover:bg-white/90 h-12 w-full sm:w-auto"
                            data-testid="fullscreen-continue-button">
                            Continue
                        </Button>
                        <span className="text-xs text-white/60">
                            You can open this notice again any time under Notices.
                        </span>
                    </div>
                )}
            </div>
        </div>
    );
};
